const CHECKOUT_STORAGE_KEY = "ion.pendingCheckout";
let inMemoryCheckout = null;

export async function requestJSON(url, { timeoutMs = 30000, signal, ...options } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort(signal.reason);
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new DOMException("Request timed out", "TimeoutError")),
    timeoutMs,
  );
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (response.status === 204) return null;
    const data = await response.json().catch(() => null);
    if (controller.signal.aborted) throw controller.signal.reason;
    if (!response.ok) {
      const error = new Error(data?.message || data?.detail || "Request failed. Please try again.");
      error.status = response.status;
      throw error;
    }
    if (data === null) throw new Error("The server returned an invalid response.");
    return data;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

export function safeCheckoutURL(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      ["secure.peachpayments.com", "testsecure.peachpayments.com"].includes(url.host) &&
      !url.username && !url.password && (url.pathname === "/checkout" || url.pathname.startsWith("/checkout/"));
  } catch {
    return false;
  }
}

function validContext(context) {
  return Number.isSafeInteger(context?.id) && context.id > 0 &&
    typeof context.token === "string" && context.token.length > 0 && context.token.length <= 4096;
}

export function savePendingCheckout(context, storage) {
  if (!validContext(context)) return false;
  // A restored page can recover the checkout even when sessionStorage is blocked.
  inMemoryCheckout = { id: context.id, token: context.token };
  try {
    (storage ?? window.sessionStorage).setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(inMemoryCheckout));
    return true;
  } catch {
    // The signed browser-return URL still supports recovery when storage is disabled.
    return false;
  }
}

export function readPendingCheckout(url, storage) {
  const parsed = new URL(url);
  const context = {
    id: Number(parsed.searchParams.get("checkout_id")),
    token: parsed.searchParams.get("checkout_token"),
  };
  if (validContext(context)) {
    savePendingCheckout(context, storage);
    return context;
  }
  try {
    const saved = JSON.parse((storage ?? window.sessionStorage).getItem(CHECKOUT_STORAGE_KEY));
    if (validContext(saved)) {
      inMemoryCheckout = { id: saved.id, token: saved.token };
      return inMemoryCheckout;
    }
  } catch {
    // The current document may still have its checkout context after browser Back.
  }
  return inMemoryCheckout;
}

export function clearPendingCheckout(storage) {
  inMemoryCheckout = null;
  try { (storage ?? window.sessionStorage).removeItem(CHECKOUT_STORAGE_KEY); } catch { /* Optional storage. */ }
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, ms);
    if (signal?.aborted) abort();
    else signal?.addEventListener("abort", abort, { once: true });
  });
}

export async function pollCheckout({ portalURL, context, signal, onUpdate, attempts = 20, intervalMs = 2000 }) {
  const deadline = Date.now() + 60000;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (Date.now() >= deadline) return "pending";
    const data = await requestJSON(
      `${portalURL}peach-payment-status/${context.id}?token=${encodeURIComponent(context.token)}`,
      { signal, timeoutMs: Math.min(8000, deadline - Date.now()) },
    );
    onUpdate?.(data);
    if (data.processed === true) return "success";
    if (data.processing === false) return "failed";
    if (attempt + 1 < attempts) await wait(Math.min(intervalMs, Math.max(0, deadline - Date.now())), signal);
  }
  return "pending";
}
